import { Component, OnInit, OnDestroy } from '@angular/core';
import { IndicatorCalculationService } from '../../service/IndicatorCalculation.service';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { BaseComponent } from 'src/app/base/base.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IndicatorCalculation } from 'src/app/model/indicatorCalculation';

@Component({
    selector: 'app-sustainability',
    templateUrl: './sustainability.component.html',
    styleUrls: ['./sustainability.component.scss']
})
export class SustainabilityComponent extends BaseComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    IndicatorCalculationList: any[] = [];
    filteredIndicatorCalculationList: any[] = [];
    IndicatorCalculationData: any = {};
    searchTerm: string = '';
    indicatorCalculation: IndicatorCalculation = new IndicatorCalculation();

    indicatorOptions: string[] = [];
    reductionTypeOptions: string[] = [];
    metricOptions: string[] = [];

    constructor(
        private indicatorCalculationService: IndicatorCalculationService,
        translate: TranslateService,
        loading: NgxSpinnerService,
        router: Router
    ) {
        super(router, loading, translate);
    }

    ngOnInit(): void {
        this.loadIndicatorCalculationData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadIndicatorCalculationData(): void {
        this.loading.show();
        this.indicatorCalculationService.getIndicatorCalculations()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response && response.getIndicatorCalculations) {
                        this.IndicatorCalculationList = response.getIndicatorCalculations.map(item => ({
                            id: item.id,
                            indicator: this.translate.instant(item.indicator) || item.indicator,
                            reductionType: this.translate.instant(item.reductionType) || item.reductionType,
                            metric: this.translate.instant(item.metric) || item.metric,
                            conversionFactor: item.conversionFactor,
                        }));

                        this.filteredIndicatorCalculationList = [...this.IndicatorCalculationList];

                        this.indicatorOptions = Array.from(new Set(this.IndicatorCalculationList.map(item => item.indicator)));
                        this.reductionTypeOptions = Array.from(new Set(this.IndicatorCalculationList.map(item => item.reductionType)));
                        this.metricOptions = Array.from(new Set(this.IndicatorCalculationList.map(item => item.metric)));
                    }
                    this.loading.hide();
                },
                error: (error) => {
                    this.loading.hide();
                    this.showMessage('Atenção', 'Erro ao carregar os critérios de sustentabilidade.');
                },
            });
    }

    search(): void {
        if (!this.searchTerm) {
            this.filteredIndicatorCalculationList = [...this.IndicatorCalculationList];
        } else {
            const searchTerm = this.searchTerm.toLowerCase();
            this.filteredIndicatorCalculationList = this.IndicatorCalculationList.filter(item => {
                return (
                    item.indicator.toLowerCase().includes(searchTerm) ||
                    item.reductionType.toLowerCase().includes(searchTerm) ||
                    item.metric.toLowerCase().includes(searchTerm) ||
                    item.conversionFactor.toString().includes(searchTerm)
                );
            });
        }
    }

    newSustainability(): void {
        this.IndicatorCalculationData = {};
        this.showModal('modalSustainability');
    }

    editSustainability(indicatorCalculation: any): void {
        this.IndicatorCalculationData = {
            indicator: indicatorCalculation.indicator,
            reductionType: indicatorCalculation.reductionType,
            metric: indicatorCalculation.metric,
            conversionFactor: indicatorCalculation.conversionFactor,
        };

        this.indicatorCalculation = {
            id: indicatorCalculation.id,
            indicator: indicatorCalculation.indicator,
            reductionType: indicatorCalculation.reductionType,
            metric: indicatorCalculation.metric,
            conversionFactor: indicatorCalculation.conversionFactor,
        };

        this.showModal('modalSustainability');
    }

    saveSustainability(): void {
        const { indicator, reductionType, metric, conversionFactor } = this.IndicatorCalculationData;

        if (!indicator || !reductionType || !metric || !conversionFactor) {
            this.showMessage('Erro', 'Todos os campos precisam ser preenchidos.');
            return;
        }

        const input = {
            indicator,
            reductionType,
            metric,
            conversionFactor: Number(conversionFactor),
        };

        if (this.indicatorCalculation?.id) {
            this.updateIndicatorCalculation(input);
        } else {
            this.createIndicatorCalculation(input);
        }
    }

    createIndicatorCalculation(input: any): void {
        this.indicatorCalculationService.createIndicatorCalculation({ input })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.IndicatorCalculationData = {};
                    this.loadIndicatorCalculationData();
                    this.loading.hide();
                    this.showMessage('Sucesso', 'Critério de sustentabilidade criado com sucesso!', 'success');
                    this.closeModal('modalSustainability');
                },
                error: (error) => {
                    console.error('Erro ao criar critério de sustentabilidade:', error);
                    this.loading.hide();
                    this.showMessage('Erro', 'Erro ao criar o critério de sustentabilidade.', 'error');
                }
            });
    }

    updateIndicatorCalculation(input: any): void {
        const variables = {
            id: this.indicatorCalculation.id,
            data: input,
        };

        this.indicatorCalculationService.updateIndicatorCalculation(variables)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.loadIndicatorCalculationData();
                    this.loading.hide();
                    this.showMessage('Sucesso', 'Critério de sustentabilidade atualizado com sucesso!', 'success');
                    this.closeModal('modalSustainability');
                },
                error: (error) => {
                    this.loading.hide();
                    this.showMessage('Erro', 'Erro ao atualizar o critério de sustentabilidade.', 'error');
                }
            });
    }

    deleteSustainability(id: number): void {
        this.loading.show();
        this.indicatorCalculationService.deleteIndicatorCalculation(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.loadIndicatorCalculationData();
                    this.showMessage('Sucesso', 'Critério de sustentabilidade excluído com sucesso!', 'success');
                },
                error: (error) => {
                    this.showMessage('Erro', 'Erro ao excluir o critério de sustentabilidade.', error);
                }
            });
    }
}
